import { useCallback, useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
    prompt: () => Promise<void>
}

const isStandalone = (): boolean =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true

const isIOS = (): boolean =>
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream

export function usePWAInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [installed, setInstalled] = useState<boolean>(() => isStandalone())

    useEffect(() => {
        const onBeforeInstall = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
        }
        const onInstalled = () => {
            setInstalled(true)
            setDeferredPrompt(null)
        }

        window.addEventListener('beforeinstallprompt', onBeforeInstall)
        window.addEventListener('appinstalled', onInstalled)

        const mql = window.matchMedia('(display-mode: standalone)')
        const onDisplayChange = (e: MediaQueryListEvent) => e.matches && setInstalled(true)
        mql.addEventListener('change', onDisplayChange)

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall)
            window.removeEventListener('appinstalled', onInstalled)
            mql.removeEventListener('change', onDisplayChange)
        }
    }, [])

    const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
        if (!deferredPrompt) return 'unavailable'
        await deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice
        setDeferredPrompt(null)
        return outcome
    }, [deferredPrompt])

    return {
        canInstall: !installed && deferredPrompt !== null,
        isIos: !installed && isIOS(),
        installed,
        promptInstall
    }
}
